// N38 · Phase 1.2 — 活动词 → { 分类 id, 轨道 }（纯函数）
//
// 别名来源三层：① 自动从分类 name + desc 抽词；② 人工补的高频口语别名 SEED_ALIASES；
// ③ extraAliases —— Phase 2 在打卡对话里「自学习」累积的 localStorage.activityAliases。
// 匹配不中 → 返回 null，交 Phase 2 追问"算哪个分类？"，答案再写回 extraAliases。

import { CATEGORIES, DEFAULT_CUSTOM_CATS, CAT_TREE } from '../data/categories.js';

// ---- 分类 → 轨道（0 娱乐·吃 / 1 睡眠 / 2 运动），按 root 归类 ----
const PARENT = {};
CAT_TREE.forEach(n => (n.children || []).forEach(c => { PARENT[c] = n.id; }));
DEFAULT_CUSTOM_CATS.forEach(c => { if (c.parentId) PARENT[c.id] = c.parentId; });
export function rootOf(id) { let cur = id, n = 0; while (PARENT[cur] && n++ < 16) cur = PARENT[cur]; return cur; }
export function trackForCat(id) { const r = rootOf(id); return r === 'sleep' ? 1 : r === 'exercise' ? 2 : 0; }

const ALL_CATS = [...CATEGORIES, ...DEFAULT_CUSTOM_CATS];
const NAME_OF = Object.fromEntries(ALL_CATS.map(c => [c.id, c.name || c.label || c.id]));

// 人工补充的高频别名（口语说法，自动抽词覆盖不到的）
export const SEED_ALIASES = {
  eat:        ['吃', '吃饭', '吃早饭', '吃午饭', '吃晚饭', '早饭', '早餐', '午饭', '午餐', '晚饭', '晚餐', '干饭', '用餐'],
  sleep:      ['睡', '睡觉', '睡了', '入睡', '午睡', '睡午觉', '补觉'],
  sleep_lazy: ['赖床', '卧床', '赖会床'],
  sleep_phone:['躺着没睡', '睡前刷手机', '躺床上玩手机', '没睡着'],
  exercise:   ['运动', '锻炼', '健身', '公园', '去公园', '逛公园'],
  exer_yoga:  ['瑜伽', '八段锦', '拉伸'],
  exer_foot:  ['泡脚'],
  exer_wash:  ['洗澡', '冲澡', '沐浴'],
  exer_hair:  ['洗头'],
  exer_run:   ['跑步', '晨跑', '夜跑', '慢跑'],
  prep_pr:    ['整理', '清洁', '做饭', '打扫', '收拾'],
  prep_xnx:   ['小能熊', '时间规划', '规划'],
  focus_work: ['求职', '投简历', '面试'],
  focus_en:   ['英语', '背单词', '日语', '泰语'],
  focus_py:   ['数据', '数据分析', '系统分析', 'excel'],
  fun_s:      ['拉屎', '拉稀', '拉肚子', '拉肚', '上厕所', '厕所', '大号', '蹲坑'],
  fun_book:   ['看书', '阅读', '看剧', '追剧', '看电影', '看视频'],
  fun_relax:  ['放松', '发呆', '歇会', '摸鱼'],
  social_talk:['聊天', '吵架', '互动'],
  social_tra: ['通勤', '在路上', '坐车', '开车', '走路', '赶路'],
  social_help:['帮忙'],
};

// 组装「别名 → catId」索引：自动（name + desc 分词）+ 人工 SEED + 运行时 extra
// 优先级：extra(2 自学习/用户教的) > seed(1 人工预置) > auto(0 自动抽词) ——
// Phase 2「改一下」纠正映射靠这个生效：同一个词用户教过新分类后，必须盖过预置。
function buildAliasIndex(extra) {
  const idx = [];
  const add = (alias, catId, pri) => {
    const a = String(alias).trim().toLowerCase();
    if (a) idx.push({ alias: a, catId, pri });
  };
  for (const c of ALL_CATS) {
    if (c.name) add(c.name, c.id, 0);
    if (c.desc) c.desc.split(/[\/、，,;；]/).forEach(tok => {
      const t = tok.trim();
      if (t && !/\s/.test(t)) add(t, c.id, 0);
    });
  }
  for (const [catId, list] of Object.entries(SEED_ALIASES)) (list || []).forEach(a => add(a, catId, 1));
  for (const [catId, list] of Object.entries(extra || {})) (list || []).forEach(a => add(a, catId, 2));
  // 去重 + 排序：长别名优先（避免"头"先于"洗头"、"拉"先于"拉伸"命中），同长按优先级降序
  const seen = new Set();
  const uniq = idx.filter(x => {
    const k = x.alias + '|' + x.catId;
    if (seen.has(k)) return false;
    seen.add(k); return true;
  });
  uniq.sort((a, b) => (b.alias.length - a.alias.length) || (b.pri - a.pri));
  return uniq;
}

// 在文本里匹配活动 → { catId, track, name, matchedAlias }，不中返回 null。
// extraAliases：Phase 2 自学习累积的 { catId: [别名...] }。
export function matchActivity(text, { extraAliases = {} } = {}) {
  if (!text) return null;
  const t = String(text).toLowerCase();
  const idx = buildAliasIndex(extraAliases);
  for (const { alias, catId } of idx) {
    // 单字别名（吃/拉/睡…）只在词首/词尾匹配，避免"扒拉饭"里的"拉"被误命中；
    // 多字别名走子串匹配。
    const hit = alias.length === 1 ? (t.startsWith(alias) || t.endsWith(alias)) : t.includes(alias);
    if (hit) {
      return { catId, track: trackForCat(catId), name: NAME_OF[catId] || catId, matchedAlias: alias };
    }
  }
  return null;
}
